// DesktopGallery — left-column composition on the desktop detail
// surface. Renders the primary image with overlays (QuickStatus +
// internalCode badges, image counter, lightbox launcher) plus the
// 5-tile thumbnail strip beneath it. Side arrows step through the
// gallery; the lightbox dialog uses the shared `PropertyLightbox`.

import { useState } from "react";
import { Camera, ChevronLeft, ChevronRight, Eye } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { AuthImg } from "@/components/auth-img";
import { cn } from "@/lib/utils";
import PropertyQuickStatusButton from "@/features/property/components/property-quick-status-button";
import { PropertyLightbox } from "@/features/property/components/detail/property-lightbox";
import type { Property, PropertyImage } from "@/types/property";

interface DesktopGalleryProps {
  property: Property;
  images: PropertyImage[];
  quickStatusReadOnly: boolean;
}

export function DesktopGallery({
  property,
  images,
  quickStatusReadOnly,
}: DesktopGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const safeIndex =
    images.length === 0 ? 0 : Math.min(activeIndex, images.length - 1);
  const activeImage = images[safeIndex];
  const goPrev = () => {
    if (images.length === 0) return;
    setActiveIndex((idx) => (idx - 1 + images.length) % images.length);
  };
  const goNext = () => {
    if (images.length === 0) return;
    setActiveIndex((idx) => (idx + 1) % images.length);
  };

  // Thumbnail strip: show up to 5 cards; the 5th tile reveals overflow.
  const thumbs = images.slice(0, 5);
  const overflow = Math.max(0, images.length - 5);

  return (
    <>
      {/* Primary image */}
      <div className="relative flex-1 overflow-hidden rounded-[14px] bg-muted">
        {activeImage ? (
          <AuthImg
            src={activeImage.fullUrl}
            alt={activeImage.originalFilename || property.buildingName || ""}
            loading="eager"
            className="h-full w-full object-cover"
          />
        ) : property.thumbnailUrl ? (
          // No uploaded photos yet → show the seeded placeholder (never stored;
          // a real upload replaces it) so the hero isn't an empty box.
          <img
            src={property.thumbnailUrl}
            alt=""
            loading="eager"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <Camera aria-hidden="true" className="size-12 opacity-40" />
          </div>
        )}

        {/* Top-left overlays: QuickStatus + internalCode */}
        <div className="absolute top-3.5 left-3.5 flex items-center gap-1.5">
          <PropertyQuickStatusButton
            property={property}
            readOnly={quickStatusReadOnly}
            className="bg-white/95 backdrop-blur-sm"
          />
          {property.internalCode !== null ? (
            <Badge
              variant="neutral"
              className="bg-white/95 font-mono text-[11px] backdrop-blur-sm"
            >
              {property.internalCode}
            </Badge>
          ) : null}
        </div>

        {/* Bottom-right counter */}
        {images.length > 0 ? (
          <div className="absolute right-3.5 bottom-3.5">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 font-mono text-[11px] text-white">
              <Camera aria-hidden="true" className="size-[11px]" />
              {String(safeIndex + 1).padStart(2, "0")} / {images.length}
            </span>
          </div>
        ) : null}

        {/* Bottom-left lightbox launcher */}
        {images.length > 0 ? (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className="absolute bottom-3.5 left-3.5 inline-flex items-center gap-1.5 rounded-lg bg-black/60 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-black/70"
          >
            <Eye aria-hidden="true" className="size-[11px]" />
            Xem lightbox
          </button>
        ) : null}

        {/* Side arrows */}
        {images.length > 1 ? (
          <>
            <button
              type="button"
              onClick={goPrev}
              aria-label="Ảnh trước"
              className="absolute top-1/2 left-3.5 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-foreground shadow-sm hover:bg-white"
            >
              <ChevronLeft aria-hidden="true" className="size-4" />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Ảnh sau"
              className="absolute top-1/2 right-3.5 flex size-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-foreground shadow-sm hover:bg-white"
            >
              <ChevronRight aria-hidden="true" className="size-4" />
            </button>
          </>
        ) : null}
      </div>

      {/* Thumbnail strip */}
      {images.length > 0 ? (
        <div className="grid h-20 shrink-0 grid-cols-5 gap-2">
          {thumbs.map((image, i) => {
            const isLastTile = i === 4 && overflow > 0;
            const isActive = i === safeIndex;
            return (
              <button
                key={image.id}
                type="button"
                onClick={() => setActiveIndex(i)}
                aria-label={`Xem ảnh ${i + 1}`}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "relative overflow-hidden rounded-lg",
                  isActive ? "ring-2 ring-primary" : "",
                )}
              >
                <AuthImg
                  src={image.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                {isLastTile ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 font-mono text-sm font-bold text-white">
                    +{overflow}
                  </div>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* Lightbox dialog — shared with the mobile hero via PropertyLightbox.
          Desktop variant: shadcn default close button, `sm:max-w-5xl`,
          `h-[80vh]` viewport. */}
      <PropertyLightbox
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        images={images}
        index={safeIndex}
        onPrev={goPrev}
        onNext={goNext}
      />
    </>
  );
}
